import { Box } from '@chakra-ui/react';
import { AiChatMessage, ChatMessageFeedbackResponse } from '@worknet/models';
import { ComponentProps } from 'react';

import { parseFullChat } from '~/shared/utils/parsers';
import { useResolvedSources } from '~/shared/utils/useResolvedSources';

import ServerMessage from '../../ds/ServerMessage';
import WnMarkdownUiComponents from '../../ds/WnMarkdownUiComponents';
import { createTagGroups } from '../utils';

import { formatMessageWithCitationMarkers, getCitationsButtonsData } from './utils';
import CitationsButtons from './CitationsButtons';

type Props = {
  msg: AiChatMessage & { feedbacks?: ChatMessageFeedbackResponse[] };
  chatId?: string;
  disclaimer?: string;
  isLastMessage: boolean;
  isChatOwner: boolean;
  isCopyButtonHidden: boolean;
  interactionLoading: boolean;
  onSubmit: NonNullable<ComponentProps<typeof ServerMessage>['onSubmit']>;
};

/**
 * Its own component rather than an inline branch because citation sources now have to be
 * resolved through a hook: uploaded files carry a reference the server turns into a
 * short-lived link, and pre-#2758 answers carry a stale public URL that has to be dropped.
 */
export default function AiMessageBody({
  msg,
  chatId,
  disclaimer,
  isLastMessage,
  isChatOwner,
  isCopyButtonHidden,
  interactionLoading,
  onSubmit,
}: Props) {
  const reply = 'error' in msg.content ? undefined : msg.content;
  const isError = !reply;
  const {
    content,
    sources: parsedSources,
    components,
  } = parseFullChat(formatMessageWithCitationMarkers(msg));

  const rawSources = parsedSources.length > 0 ? parsedSources : (reply?.sources ?? []);
  const sources = useResolvedSources(chatId, msg.id, rawSources);

  const citationsButtonsData = getCitationsButtonsData(
    sources.map((source, idx) => ({
      paragraph: '',
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      citations: [{ citation: '', sourceIdx: (source.id as any) ?? idx }],
    })),
    sources
  );
  const citationsButtonsDataMap = Object.fromEntries(
    citationsButtonsData.map((data) => [data[0].toolTip.sourceIdx.toString(), data])
  );

  const inlineCitations = getCitationsButtonsData(reply?.citations, sources).map(
    (buttonsData, idx) => <CitationsButtons key={idx} buttonsData={buttonsData} />
  );

  return (
    <ServerMessage
      hiddenButtons={
        !isLastMessage || !isChatOwner ? 'all' : isCopyButtonHidden ? 'copy' : undefined
      }
      messageId={msg.id}
      message={disclaimer ? `${content}\n\n${disclaimer}` : content}
      isError={isError}
      components={{
        span: ({ index }) =>
          inlineCitations.length > 0 ? (
            <Box as="span" data-is-citation="true">
              {inlineCitations[index ?? 0] || null}
            </Box>
          ) : null,
        cite: ({ children }) => {
          const sourceIdx = children[0]?.toString();
          if (!sourceIdx || !citationsButtonsDataMap[sourceIdx]) {
            return null;
          }
          return <CitationsButtons buttonsData={citationsButtonsDataMap[sourceIdx]} />;
        },
      }}
      liked={msg?.feedbacks?.[0]?.isHelpful}
      loading={interactionLoading}
      onSubmit={onSubmit}
      tagGroups={createTagGroups(isError ? [] : sources)}
    >
      <WnMarkdownUiComponents components={components} interactionsDisabled={!isLastMessage} />
    </ServerMessage>
  );
}
